<template>
  <div class="tenant-management p-4">
    <admin-nav />
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2>
        <i class="fas fa-building me-2"></i>
        Tenant Management
      </h2>
      <button class="btn btn-primary" @click="showCreateModal = true">
        <i class="fas fa-plus me-2"></i>
        Create Tenant
      </button>
    </div>

    <!-- Tenants List -->
    <div class="card">
      <div class="card-body">
        <div class="table-responsive">
          <table class="table table-striped table-hover">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Schema</th>
                <th>Subscription Tier</th>
                <th>Status</th>
                <th>Users / Clients</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="tenant in tenants" :key="tenant.TenantId">
                <td>
                  <div class="fw-bold">{{ tenant.TenantName }}</div>
                  <small class="text-muted">{{ tenant.TenantKey }}</small>
                </td>
                <td><code>{{ tenant.SchemaName }}</code></td>
                <td>
                  <span class="badge bg-info">{{ tenant.SubscriptionTierName }}</span>
                </td>
                <td>
                  <span class="badge" :class="tenant.IsActive ? 'bg-success' : 'bg-secondary'">
                    {{ tenant.IsActive ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td>
                  <small>{{ tenant.MaxUsers }} / {{ tenant.MaxClients }}</small>
                </td>
                <td>
                  <small>{{ formatDate(tenant.CreatedDate) }}</small>
                </td>
                <td>
                  <div class="btn-group btn-group-sm">
                    <button class="btn btn-outline-primary" @click="editTenant(tenant)" title="Edit">
                      <i class="fas fa-edit"></i>
                    </button>
                    <button class="btn btn-outline-info" @click="viewUsage(tenant)" title="Usage">
                      <i class="fas fa-chart-bar"></i>
                    </button>
                    <button class="btn btn-outline-warning" @click="startImpersonation(tenant)" title="Impersonate">
                      <i class="fas fa-user-secret"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div class="modal" :class="{ 'show d-block': showCreateModal || showEditModal }" v-if="showCreateModal || showEditModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ showCreateModal ? 'Create Tenant' : 'Edit Tenant' }}</h5>
            <button type="button" class="btn-close" @click="closeModals"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveTenant">
              <div class="row">
                <div class="col-md-6 mb-3" v-if="showCreateModal">
                  <label class="form-label">Tenant Key *</label>
                  <input type="text" class="form-control" v-model="formData.tenantKey" required>
                  <small class="text-muted">URL-safe identifier (e.g., 'acme', 'widget-co')</small>
                </div>
                <div class="col-md-6 mb-3" v-if="showCreateModal">
                  <label class="form-label">Schema Name *</label>
                  <input type="text" class="form-control" v-model="formData.schemaName" required>
                  <small class="text-muted">SQL Server schema name</small>
                </div>
                <div class="col-md-12 mb-3">
                  <label class="form-label">Tenant Name *</label>
                  <input type="text" class="form-control" v-model="formData.tenantName" required>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">Subscription Tier *</label>
                  <select class="form-select" v-model="formData.subscriptionTierId" required>
                    <option v-for="tier in subscriptionTiers" :key="tier.subscriptionTierId" :value="tier.subscriptionTierId">
                      {{ tier.tierName }} - ${{ tier.monthlyPrice }}/mo
                    </option>
                  </select>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">Square Mode</label>
                  <select class="form-select" v-model="formData.squareMode">
                    <option value="sandbox">Sandbox</option>
                    <option value="live">Live</option>
                  </select>
                </div>
                <div class="col-md-12 mb-3" v-if="!showEditModal">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" v-model="formData.provisionSchema" id="provisionSchema">
                    <label class="form-check-label" for="provisionSchema">
                      Automatically provision schema and run migrations
                    </label>
                  </div>
                </div>
                <div class="col-md-12 mb-3" v-if="showEditModal">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" v-model="formData.isActive" id="isActive">
                    <label class="form-check-label" for="isActive">
                      Active
                    </label>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModals">Cancel</button>
            <button type="button" class="btn btn-primary" @click="saveTenant" :disabled="saving">
              <i class="fas fa-spinner fa-spin me-2" v-if="saving"></i>
              {{ showCreateModal ? 'Create' : 'Update' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show" v-if="showCreateModal || showEditModal"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import ApiService from '@/services/ApiService';
import Cache from '@/services/Cache';
import AdminNav from '@/components/Shared/AdminNav.vue';

const tenants = ref<any[]>([]);
const subscriptionTiers = ref<any[]>([]);
const showCreateModal = ref(false);
const showEditModal = ref(false);
const saving = ref(false);

const formData = ref({
  tenantId: 0,
  tenantKey: '',
  tenantName: '',
  schemaName: '',
  subscriptionTierId: 0,
  squareMode: 'sandbox',
  provisionSchema: true,
  isActive: true
});

const loadTenants = async () => {
  try {
    tenants.value = await ApiService.loadTenants(true);
  } catch (error: any) {
    console.error('Error loading tenants:', error);
  }
};

const loadSubscriptionTiers = async () => {
  try {
    subscriptionTiers.value = await ApiService.loadSubscriptionTiers(true);
  } catch (error: any) {
    console.error('Error loading subscription tiers:', error);
  }
};

const editTenant = (tenant: any) => {
  formData.value = {
    tenantId: tenant.TenantId,
    tenantKey: tenant.TenantKey,
    tenantName: tenant.TenantName,
    schemaName: tenant.SchemaName,
    subscriptionTierId: tenant.SubscriptionTierId,
    squareMode: tenant.SquareMode || 'sandbox',
    provisionSchema: false,
    isActive: tenant.IsActive
  };
  showEditModal.value = true;
};

const saveTenant = async () => {
  saving.value = true;
  try {
    await ApiService.saveTenant(formData.value);
    closeModals();
    await loadTenants();
  } catch (error: any) {
    console.error('Error saving tenant:', error);
  } finally {
    saving.value = false;
  }
};

const viewUsage = async (tenant: any) => {
  try {
    const usage = await ApiService.loadTenantUsage(tenant.TenantId, true);
    alert(`Usage for ${tenant.TenantName}:\n\nTechnicians: ${usage.CurrentTechnicians} / ${usage.MaxTechnicians}\nClients: ${usage.CurrentClients} / ${usage.MaxClients}\nMonthly Requests: ${usage.MonthlyRequests}${usage.MaxMonthlyRequests ? ' / ' + usage.MaxMonthlyRequests : ' (unlimited)'}`);
  } catch (error: any) {
    console.error('Error loading usage:', error);
    alert('Error loading usage data');
  }
};

const startImpersonation = async (tenant: any) => {
  if (!confirm(`Start impersonating ${tenant.TenantName}? You will be logged into their tenant context.`)) {
    return;
  }
  
  try {
    const response = await ApiService.startImpersonation(tenant.TenantId);
    const token = response?.Token || response;
    
    if (!token) {
      throw new Error('No token received from server');
    }
    
    // Store new impersonation token using Cache service
    await Cache.set('bearer', token);
    
    // Decode and store minimal user info needed for the session
    // The token contains all necessary claims for impersonation
    await Cache.set('radar_user', {
      Token: token,
      Email: null,
      UserId: null,
      StaffId: null
    });
    
    alert(`Now impersonating ${tenant.tenantName}. Reloading...`);
    window.location.reload();
  } catch (error: any) {
    console.error('Error starting impersonation:', error);
    alert('Error starting impersonation: ' + (error.response?.data?.message || error.message));
  }
};

const closeModals = () => {
  showCreateModal.value = false;
  showEditModal.value = false;
  formData.value = {
    tenantId: 0,
    tenantKey: '',
    tenantName: '',
    schemaName: '',
    subscriptionTierId: 0,
    squareMode: 'sandbox',
    provisionSchema: true,
    isActive: true
  };
};

const formatDate = (dateString: string) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString();
};

onMounted(() => {
  loadTenants();
  loadSubscriptionTiers();
});
</script>

<style scoped>
.tenant-management {
  max-width: 1400px;
  margin: 0 auto;
}

.modal.show {
  background-color: rgba(0, 0, 0, 0.5);
}
</style>
